/**
 * Typed bindings for /api/library — the company's content library: ready-made
 * texts and files an operator answers with, in folders, shared by the team.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type LibraryItemKind = "text" | "photo" | "video" | "voice" | "audio" | "document";

export interface LibraryFolder {
  id: string;
  name: string;
}

export interface LibraryItem {
  id: string;
  kind: LibraryItemKind;
  title: string;
  /** The material itself for a text one; the caption for a file. May hold spintax. */
  text: string | null;
  folder_id: string | null;
  /** A reply template: offered in the quick menu beside the reply field. */
  is_quick: boolean;
  file_name: string | null;
  file_mime: string | null;
  file_size: number;
  /** A voice note's length. */
  duration_seconds: number | null;
  /** The login of whoever added it; null once that user is gone. */
  created_by: string | null;
  updated_at: string;
}

export interface Library {
  folders: LibraryFolder[];
  items: LibraryItem[];
  /** How much of the company's room for files is taken, and how much there is. */
  used_bytes: number;
  quota_bytes: number;
  max_file_bytes: number;
}

const LIBRARY_KEY = ["library"] as const;

/** The whole library at once — it is small, and the page filters it itself. */
export function useLibrary(enabled = true) {
  return useQuery<Library>({
    queryKey: LIBRARY_KEY,
    queryFn: () => api.get<Library>("/api/library"),
    enabled,
    // A colleague may be adding materials meanwhile.
    staleTime: 30_000,
  });
}

/** Where a file material's bytes are — for a thumbnail, a player, a download. */
export function libraryFileUrl(itemId: string): string {
  return `/api/library/items/${itemId}/file`;
}

export interface NewLibraryItem {
  title?: string;
  text?: string;
  folder_id?: string | null;
  is_quick?: boolean;
  /** Without one the material is a text. */
  file?: File | null;
  /** Make an audio file a voice note (re-encoded by the server). */
  as_voice?: boolean;
}

export function useCreateLibraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (item: NewLibraryItem) => {
      const form = new FormData();
      if (item.title) form.set("title", item.title);
      if (item.text) form.set("text", item.text);
      if (item.folder_id) form.set("folder_id", item.folder_id);
      if (item.is_quick) form.set("is_quick", "true");
      if (item.file) form.set("file", item.file);
      if (item.file && item.as_voice) form.set("as_voice", "true");
      return api.upload<LibraryItem>("/api/library/items", form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIBRARY_KEY }),
  });
}

export function useUpdateLibraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: {
      id: string;
      title?: string;
      text?: string | null;
      folder_id?: string | null;
      is_quick?: boolean;
    }) => api.patch<LibraryItem>(`/api/library/items/${id}`, patch),
    onSuccess: (updated) => {
      qc.setQueryData<Library>(
        LIBRARY_KEY,
        (library) =>
          library && {
            ...library,
            items: library.items.map((i) => (i.id === updated.id ? updated : i)),
          },
      );
      qc.invalidateQueries({ queryKey: LIBRARY_KEY });
    },
  });
}

export function useDeleteLibraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/library/items/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIBRARY_KEY }),
  });
}

export function useSaveLibraryFolder() {
  const qc = useQueryClient();
  return useMutation({
    /** With an `id` the folder is renamed, without one created. */
    mutationFn: ({ id, name }: { id?: string; name: string }) =>
      id
        ? api.patch<LibraryFolder>(`/api/library/folders/${id}`, { name })
        : api.post<LibraryFolder>("/api/library/folders", { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIBRARY_KEY }),
  });
}

export function useDeleteLibraryFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/library/folders/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIBRARY_KEY }),
  });
}

/** The folder filter: everything, what is in no folder, or one folder's id. */
export type FolderFilter = "all" | "none" | string;

/**
 * The materials a folder choice and a search leave. The search looks at the
 * title, the text and the file's name, in any letter case; every word typed
 * has to be found somewhere in them.
 */
export function filterLibrary(
  items: LibraryItem[],
  folder: FolderFilter,
  query: string,
): LibraryItem[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  return items.filter((item) => {
    if (folder === "none" ? item.folder_id !== null : folder !== "all" && item.folder_id !== folder)
      return false;
    const haystack = `${item.title}\n${item.text ?? ""}\n${item.file_name ?? ""}`.toLowerCase();
    return words.every((word) => haystack.includes(word));
  });
}

const SPINTAX = /\{([^{}]*\|[^{}]*)\}/;

/**
 * Pick one variant of every `{a|b|c}` group — so that a text taken from the
 * library fifty times does not go out fifty times the same. The rule is the
 * campaigns' own (`_expand_spintax` on the server): innermost group first, so
 * nested ones resolve; braces with no `|` inside are left as they are.
 */
export function expandSpintax(text: string, random: () => number = Math.random): string {
  let out = text;
  for (;;) {
    const group = SPINTAX.exec(out);
    if (group === null) return out;
    const options = group[1].split("|");
    const pick = options[Math.min(options.length - 1, Math.floor(random() * options.length))];
    out = out.slice(0, group.index) + pick + out.slice(group.index + group[0].length);
  }
}
